import React, { useState, useCallback, useEffect } from "react";
import {
  BREAKPOINT,
  COLORS,
  BORDER_RADIUS,
  BORDER_RADIUS_LARGE,
} from "utils/styles";
import { HomepageContainer } from "components/PageContainer";
import Head from "next/head";
import Nav from "components/Nav/Nav";
import SecondaryNav from "components/Nav/SecondaryNav";
import styled from "styled-components";
import WhatIsAnAudienceCard from "components/OverviewCarousels/WhatIsAnAudienceCard";
import AudienceGrid from "components/AudienceGrid";
import Quiz from "components/Quiz";
import {
  ToggleButton as BSToggleButton,
  ToggleButtonGroup as BSToggleButtonGroup,
} from "react-bootstrap";
import ComparingAudiences from "components/ComparingAudiences";
import Section from "components/Section";
import { useMediaQuery } from "react-responsive";

import {
  SecondaryMenuWrapper,
  SecondaryMenuButton,
  SecondaryMenuGroup,
} from "components/Nav/components";
import FlexSpacer from "components/FlexSpacer";
import Link from "next/link";

const TopYoutube = "Top Youtube Channels";
const TopMusic = "Top Music Genres";
const TopDevices = "Top Devices";

const Container = styled(HomepageContainer)`
  padding: 0 1rem 2rem 1rem;
  max-width: 80rem;
  margin: 0 auto;
`;

const QuizButton = styled.a`
  ${BORDER_RADIUS};
  background-color: ${COLORS.GRAY};
  text-decoration: none;
  color: ${COLORS.BLACK};
  font-size: 14pt;
  padding: 8px 15px 8px 15px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.25s;
  display: block;
  width: fit-content;
  margin: 2rem auto 0 auto;
  opacity: 1 !important;

  &:hover {
    color: ${COLORS.DARK_BLACK};
    text-decoration: none;
    opacity: 0.5 !important;
  }

  @media ${BREAKPOINT.M} {
    font-size: 1rem;
    width: fit-content;
    margin-left: -0.1rem;

    display: block;
    filter: drop-shadow(0px 0px 0px rgba(0, 0, 0, 0));
  }
`;

const QuizPage = styled.section`
  padding: 1rem;
`;

const WordSpiderContainer = styled.div`
  max-width: 60rem;
  margin: 0 auto;
`;

const WordSpiderGraph = styled.div`
  width: 100%;
`;

const Image = styled.img`
  width: 100%;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-bottom: 3rem;
`;

const ToggleButtonGroup = styled(BSToggleButtonGroup)`
  background: #f2f2f2;
  padding: 0.5rem;
  border-radius: 25px;
  margin-top: 2rem;
`;
const ToggleButton = styled(BSToggleButton)`
  background: #f2f2f2;
  max-width: 200px;
  margin-right: 1rem;
  border-radius: 15px !important;
  box-shadow: none !important;
  border: none !important;

  &:last-child {
    margin-right: 0;
  }

  &.active {
    background-color: #fff !important;
    font-family: "mabry-medium", sans-serif !important;
    font-weight: 500 !important;
  }
`;

export async function getStaticProps() {
  return {
    props: {
      baseUrl: process.env.BASE_URL,
    },
  };
}

export default function Audiences(props) {
  const { baseUrl } = props;
  const [showQuiz, setShowQuiz] = useState(false);
  const isMobile = useMediaQuery({ query: BREAKPOINT.M });

  const handleQuiz = () => {
    if (!showQuiz) {
      setShowQuiz(true);
    } else {
      setShowQuiz(false);
    }
    document.body.style.overflow = !showQuiz ? `hidden` : `unset`;
  };

  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      setShowQuiz(false);
    }
  });

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);

  return (
    <div>
      <Head>
        <title>Audiences for Poverty Narratives</title>
        <link rel="icon" href="/favcon2.png" />
        <meta
          property="og:image"
          content={`${baseUrl}/images/open-graph.png`}
        />
        <meta
          property="twitter:image"
          content={`${baseUrl}/images/open-graph.png`}
        />
      </Head>
      <main>
        <Nav
          breadCrumbs={[<a href="/audiences">Audiences</a>]}
          secondaryNav={
            !isMobile && (
              <SecondaryMenuWrapper>
                <FlexSpacer />
                <SecondaryMenuGroup>
                  <SecondaryMenuButton
                    href="#the-audiences"
                    text="The Audiences"
                    icon="category"
                  />
                  <SecondaryMenuButton
                    href="#whats-an-audience"
                    text="What's an Audience?"
                    icon="help"
                  />
                  <SecondaryMenuButton
                    href="#comparisons"
                    text="Comparisons"
                    icon="tonality"
                  />
                </SecondaryMenuGroup>
              </SecondaryMenuWrapper>
            )
          }
        />
        <Container>
          <Section>
            <a name="the-audiences"></a>
            <h1>4 Values-Distinct Audiences</h1>
            <p>
              Understanding people’s media habits gives us a deeper view into
              who they are, the values they hold, and how to engage them.
              Experience the cultural worlds of 4 U.S. audiences through the
              lens of the real-life media artifacts they engage with.
            </p>
            <QuizButton noIcon href="#quiz" onClick={() => handleQuiz()}>
              What audience are you? Take a quiz.
            </QuizButton>
            <footer>
              <AudienceGrid />
            </footer>
          </Section>

          <Section>
            <a name="whats-an-audience"></a>
            <h1>What's an Audience?</h1>
            <p>
              Our audiences center values and the media people actually
              experience.
            </p>
            <footer>
              <WhatIsAnAudienceCard />
            </footer>
          </Section>

          <Section>
            <a name="comparisons"></a>
            <h1>Comparing Audiences</h1>
            <p>
              See how these 4 audiences compare with regard to their cultural
              consumption.
            </p>

            <ComparingAudiences />
          </Section>
          <Section>
            <h1>Choose an Audience to Learn More</h1>
            <QuizButton noIcon href="#quiz" onClick={() => setShowQuiz(true)}>
              Curious what audience you are in? Take a quiz to find out.
            </QuizButton>
            {showQuiz ? (
              <Quiz
                onExitClick={() => {
                  handleQuiz();
                }}
              />
            ) : null}
            <footer>
              <AudienceGrid noWrap />
            </footer>
          </Section>
        </Container>
        <SecondaryNav />
      </main>
    </div>
  );
}
